<template>
  <a-modal
    id="merge-videos-modal"
    v-model:visible="showShortCuts"
    title="Shortcuts"
    destroy-on-close
    :footer="null"
  >
    <a-table
      :columns="columns"
      :data-source="mergeVideosShortcuts"
      :pagination="false"
    >
      <a-table-column title="Shortcut" data-index="shortcut" />
      <a-table-column title="Key" data-index="key" />
    </a-table>
  </a-modal>
  <a-row
    v-if="!loading"
    class="mt-3"
    style="height: 10vh; width: 100vw"
    :gutter="28"
  >
    <a-col :span="24" class="d-flex align-items-center justify-content-between">
      <div class="d-flex align-items-center">
        <h5>Merge Videos</h5>
        <i
          style="cursor: pointer"
          class="mx-1 mb-2 bi bi-info-circle"
          @click="toggleShortcutModal"
        ></i>
      </div>
    </a-col>
  </a-row>

  <a-row
    v-if="!loading"
    :gutter="[4, 1]"
    class="overflow-auto"
    style="height: calc(150vh - 50px)"
  >
    <a-col :xs="24" :sm="12" :md="8" :lg="6" class="video-sidebar">
      <div class="video-list-container">
        <a-list
          v-if="videos.length"
          id="videos-list"
          size="small"
          bordered
          :data-source="videos"
          class="video-list"
          tabindex="0"
        >
          <template #header>
            <div class="video-list-header">
              <a-typography-text strong>
                Videos ({{ videos.length }})
              </a-typography-text>
              <div>
                <a-checkbox
                  v-model:checked="videoSequenceMerge"
                  :disabled="videos.length > 2"
                >
                  Video Sequence Merge
                </a-checkbox>
              </div>
            </div>
          </template>
          <template #renderItem="{ item, index }">
            <a-list-item
              :id="'video-li' + index"
              class="video-item"
              :class="{
                'selected-video': selectedVideo === item,
                'hovered-video':
                  selectedVideo !== item && hoveredVideoIndex === index,
              }"
              tabindex="-1"
              @click="selectVideo(item, index)"
              @focus="hoveredVideoIndex = index"
              @mouseover="hoveredVideoIndex = index"
              @mouseleave="hoveredVideoIndex = null"
            >
              <span class="video-item-text">
                {{ item.fileName || item.name || `Video ${index + 1}` }}
              </span>
            </a-list-item>
          </template>
        </a-list>
      </div>

      <div class="video-controls">
        <div class="control-buttons">
          <a-button
            type="primary"
            ghost
            class="control-button"
            :disabled="
              isVideoFrameDisabled || isSequenceMergeStartCycleDisabled
            "
            @click="addPoint(frameType.startFrame)"
          >
            Set Start Cycle
          </a-button>
          <a-button
            type="danger"
            ghost
            class="control-button"
            :disabled="isCycleEndDisabled"
            @click="addPoint(frameType.endFrame)"
          >
            Set End Cycle
          </a-button>
        </div>
        <a-button
          id="save-frame-btn"
          type="primary"
          class="merge-button"
          :disabled="isVideoFrameDisabled"
          @click="handleMergeVideos"
        >
          Merge Videos
        </a-button>
      </div>
    </a-col>
    <a-col :span="18" class="d-flex flex-column" style="padding: 10px">
      <div
        class="flex-grow-1 d-flex flex-column"
        style="
          height: 65vh;
          width: 100%;
          background-color: white;
          border-radius: 4px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        "
      >
        <a-spin v-if="loading" class="m-auto" size="large" />
        <template v-else>
          <template v-if="videoError">
            <a-result status="error" title="Failed to load video frames." />
          </template>
          <template v-else>
            <a-spin v-show="!videoLoaded" class="m-auto" size="large" />
            <video
              v-if="selectedVideo?.availableLocally"
              v-show="videoLoaded"
              id="annotate-video-local"
              ref="video"
              muted
              class="m-auto"
              crossorigin="anonymous"
              style="
                width: 100%;
                height: 81vh;
                object-fit: cover;
                border-radius: 4px;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
              "
              :src="selectedVideo?.localURL || selectedVideo?.fileURL"
              @canplaythrough="handleVideoLoadedData"
              @timeupdate="handleGetCurrentTime"
              @error="videoError = true"
            ></video>

            <video
              v-else
              v-show="videoLoaded"
              id="annotate-video-local"
              ref="video"
              muted
              class="m-auto"
              crossorigin="anonymous"
              style="
                width: 100%;
                height: 100%;
                object-fit: contain;
                border-radius: 4px;
              "
              :src="selectedVideo?.localURL || selectedVideo?.fileURL"
              @canplaythrough="handleVideoLoadedData"
              @timeupdate="handleGetCurrentTime"
              @error="videoError = true"
            ></video>
          </template>
        </template>
      </div>

      <div
        id="annotate-video-labelling-box"
        style="
          background-color: #fff;
          border-radius: 4px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          padding: 10px;
        "
      >
        <video-slider
          :is-playing="isPlaying"
          :total-frames="totalFrames"
          :current-frame="currentFrame"
          :video-duration="videoDuration"
          :current-time="currentTime"
          :video-points="mergeVideoPoints"
          :video-info="selectedVideo"
          :is-marking="isMarking"
          :video-single-sequence="videoSequenceMerge"
          :is-disabled="isVideoFrameDisabled"
          @toggle-video="toggleVideo"
          @frame-change="handleFrameChange"
          @set-playback-rate="updateVideoPlaybackRate"
          @remove-video-cycles="handleRemoveVideoCycles"
        />
      </div>
    </a-col>
  </a-row>

  <a-modal
    v-model:visible="sequenceMergeConfirmModal"
    title="Incorrect Video Sequence"
    ok-text="Yes"
    cancel-text="No"
    :mask-closable="false"
    :closable="false"
    :centered="true"
    @ok="processMerge(true)"
  >
    <exclamation-circle-outlined style="color: red" />
    <a-typography-title class="ml-2" :level="5" style="display: inline">
      The videos are in reverse sequence—start and end cycles will be ignored.
      Are you sure you want to proceed with merging?
    </a-typography-title>
  </a-modal>
</template>

<script>
import { ExclamationCircleOutlined } from '@ant-design/icons-vue';
import TaskRecordService from 'src/services/taskRecord';
import VideoService from 'src/services/videos';
import { mapActions, mapGetters } from 'vuex';
import getNumberOfFrames from '../../../shared/Helpers/getNumberOfFrames';
import getStepsList from '../../../shared/Helpers/getStepsList';
import getTranslatedSteps from '../../../shared/Helpers/getTranslatedSteps.js';
import { frameType, mergeVideosShortcuts } from './config';
import VideoSlider from './VideoSlider.vue';
export default {
  components: {
    VideoSlider,
    ExclamationCircleOutlined,
  },

  inject: ['toast'],

  props: {
    videos: {},
    taskId: {},
    isVisualize: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    organization: { type: String, default: '' },
    traceframes: [],
  },

  emits: ['stopLoading', 'updateLoading', 'closeModal'],

  setup() {
    return {
      mergeVideosShortcuts,
      frameType,
    };
  },

  data() {
    return {
      showShortCuts: false,
      taskName: '',
      stepsToVisualize: {},
      currentFrame: 0,
      totalFrames: Number.MAX_SAFE_INTEGER,
      videoMetaData: null,
      value: 0,
      saveFrames: [],
      markFrames: {},
      selectedFrame: null,
      hoveredFrameIndex: 0,
      framesAdded: 0,
      prevCurrectFrame: null,
      videoLoaded: false,
      videoError: false,
      selectedVideo: {},
      hoveredVideoIndex: 0,
      mergeVideoPoints: {},
      videoSequenceMerge: false,
      sequenceMergeConfirmModal: false,
      isPlaying: false,
      currentSliderPercent: 0,
      videoDuration: 0,
      currentTime: 0,
      isMarking: false,
      currentVideoIndex: 0,
    };
  },
  computed: {
    ...mapGetters(['taskDetails', 'taskStepsCount']),

    isCycleEndDisabled() {
      return (
        this.isVideoFrameDisabled ||
        this.isStartCycleNotSelected ||
        this.isSequenceMergeEndCycleDisabled
      );
    },

    isStartCycleNotSelected() {
      const fileName = this.selectedVideo.fileName;
      const filePoints = this.mergeVideoPoints[fileName];
      const output =
        !this.videoSequenceMerge &&
        filePoints &&
        filePoints[frameType.startFrame] === undefined;
      if (output === undefined) return true;
      return output;
    },

    isVideoFrameDisabled() {
      return (
        this.loading || this.isVisualize || this.videoError || !this.videoLoaded
      );
    },

    isSequenceMergeStartCycleDisabled() {
      if (!this.videoSequenceMerge || !this.selectedVideo) return false;
      return this.selectedVideo === this.videos[1];
    },

    isSequenceMergeEndCycleDisabled() {
      if (!this.videoSequenceMerge || !this.selectedVideo) return false;
      return this.selectedVideo === this.videos[0];
    },
  },

  watch: {
    videoSequenceMerge() {
      this.videos.forEach(({ fileName }) => {
        const filePoints = this.mergeVideoPoints[fileName];
        if (filePoints) {
          delete filePoints[frameType.startFrame];
          delete filePoints[frameType.endFrame];
        }
      });
    },
  },

  created() {
    window.onbeforeunload = () => '';
  },

  mounted() {
    this.selectedVideo = this.videos[0];
    this.selectedVideo = this.videos[0];
    this.setTaskDetails(this.taskDetails, true);
    this.getVideoMetaData();
    this.setisFrameUpdated(false);
    document.addEventListener('keyup', this.handleKeyUpEvents);
    document.addEventListener('keydown', this.handleKeyDownEvents);
  },

  beforeUnmount() {
    this.$emit('updateLoading', true);
    document.removeEventListener('keyup', this.handleKeyUpEvents);
    document.removeEventListener('keydown', this.handleKeyDownEvents);
    window.onbeforeunload = null;
  },

  methods: {
    ...mapActions(['setSelectedLanguage', 'setisFrameUpdated']),

    handleRemoveVideoCycles() {
      const videoName = this.selectedVideo.fileName;
      const point = this.mergeVideoPoints[videoName];
      delete point[frameType.startFrame];
      delete point[frameType.endFrame];
    },

    handleGetCurrentTime() {
      if (!this.isPlaying) {
        return;
      }
      const videoRef = this.$refs.video;
      if (!videoRef) return;
      this.currentTime = videoRef.currentTime;
      const videoFPS = this.videoMetaData?.frameRate || this.selectedVideo?.fps;
      this.currentFrame = Math.ceil(parseInt(this.currentTime * videoFPS));
      if (this.currentFrame === this.totalFrames - 1) this.isPlaying = false;
    },

    handleVideoLoadedData() {
      this.videoLoaded = true;
      const videoRef = this.$refs.video;
      this.videoDuration = videoRef ? parseInt(videoRef.duration, 10) : 0;
    },

    updateVideoPlaybackRate(videoPlaybackRate) {
      const videoRef = this.$refs.video;
      if (!videoRef) return;
      videoRef.playbackRate = videoPlaybackRate;
    },

    toggleVideo() {
      const videoRef = this.$refs.video;
      if (!videoRef) return;
      if (videoRef.paused && !this.isPlaying) {
        videoRef.play();
        this.isPlaying = true;
      } else if (this.isPlaying) {
        videoRef.pause();
        this.isPlaying = false;
      }
    },

    toggleShortcutModal() {
      this.showShortCuts = !this.showShortCuts;
    },

    handleKeyUpEvents(event) {
      if (event.key === 'Escape') {
        this.$emit('closeModal');
      }
      if (event.key === 'Delete') {
        this.handleRemoveVideoCycles();
      }
    },

    handleVideoCycleSelection() {
      if (this.isVideoFrameDisabled) return;
      if (this.isPlaying) this.toggleVideo();
      if (this.videoSequenceMerge)
        return this.handleKeyDownVideoSequenceMerge();
      const videoName = this.selectedVideo.fileName;
      const point = this.mergeVideoPoints[videoName] || {};
      const isStartMissing = !Object.prototype.hasOwnProperty.call(
        point,
        frameType.startFrame
      );
      const isEndMissing = !Object.prototype.hasOwnProperty.call(
        point,
        frameType.endFrame
      );

      if (isStartMissing) {
        return this.addPoint(frameType.startFrame);
      }

      if (isEndMissing) {
        return this.addPoint(frameType.endFrame);
      }

      const closeFrameType = this.getNearestKeyFrameType();
      if (!closeFrameType || closeFrameType === frameType.startFrame)
        return this.addPoint(frameType.startFrame);
      else if (closeFrameType === frameType.endFrame)
        return this.addPoint(frameType.endFrame);
    },

    getNearestKeyFrameType() {
      const videoName = this.selectedVideo.fileName;
      const point = this.mergeVideoPoints[videoName];

      const startFrame = point[frameType.startFrame];
      const endFrame = point[frameType.endFrame];

      const nearStart = Math.abs(this.currentFrame - startFrame);
      const nearEnd = Math.abs(this.currentFrame - endFrame);
      return nearStart <= nearEnd ? frameType.startFrame : frameType.endFrame;
    },

    async handleKeyDownEvents(event) {
      if (event.code === 'Space') this.handleVideoCycleSelection();
      const key = event.key.toLowerCase();
      if (key === 'arrowup') this.navigateFrameChange(this.currentFrame + 1);
      if (key === 'arrowdown') this.navigateFrameChange(this.currentFrame - 1);
      if (key === 'd') await this.handleSelectVideo(this.currentVideoIndex + 1);
      if (key === 'a') await this.handleSelectVideo(this.currentVideoIndex - 1);
    },

    navigateFrameChange(frame) {
      if (this.isVideoFrameDisabled) return;
      if (this.isPlaying) return;
      if (frame < 0) return;
      if (frame > this.totalFrames) return;
      this.handleFrameChange(frame);
    },

    async handleSelectVideo(index) {
      if (this.isVideoFrameDisabled) return;
      if (index < 0) return;
      if (index >= this.videos.length) return;
      this.hoveredVideoIndex = null;
      this.currentVideoIndex = index;
      await this.selectVideo(
        this.videos[this.currentVideoIndex],
        this.currentVideoIndex
      );
    },

    handleKeyDownVideoSequenceMerge() {
      const firstVideo = this.videos[0];
      if (this.selectedVideo === firstVideo) {
        this.addPoint(frameType.startFrame);
      } else this.addPoint(frameType.endFrame);
    },

    setStepTranslations(lang) {
      if (lang === this.translationLanguage) {
        this.stepsTranslationMapping = getTranslatedSteps(
          this.taskDetails,
          this.translationDetails
        );
      } else {
        this.stepsTranslationMapping = getTranslatedSteps(this.taskDetails);
      }
    },

    getCanvasBlob(canvas) {
      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob); // Resolve the Blob
          } else {
            reject('Failed to generate Blob from canvas.');
          }
        }, 'image/png');
      });
    },
    async extractVideoFrame() {
      const videoElement = this.$refs.video;
      // Ensure video dimensions are available
      if (videoElement.videoWidth > 0 && videoElement.videoHeight > 0) {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        // Set canvas dimensions to match the video
        canvas.width = videoElement.videoWidth;
        canvas.height = videoElement.videoHeight;

        // Draw the current frame of the video onto the canvas
        context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

        const blob = await this.getCanvasBlob(canvas);
        return blob;
      } else {
        console.error(
          'Video dimensions are not available. Ensure the video is loaded.'
        );
      }
    },

    handleFrameChange(frame) {
      if (this.loading) return;
      this.currentFrame = frame;
      const videoFPS = this.videoMetaData?.frameRate || this.selectedVideo?.fps;
      const frameToMove = (Number(frame) / videoFPS).toFixed(25);
      this.$refs.video.currentTime = frameToMove;
      this.currentTime = frameToMove;
      this.currentSliderPercent = this.getPercent(frame);
    },

    getPercent(frame) {
      return (frame / this.totalFrames) * 100;
    },

    async getVideoMetaData() {
      if (!this.selectedVideo) return;
      const payload = { id: this.selectedVideo?.id };
      const [error, res] = await TaskRecordService.getVideoMetaData(
        payload,
        false
      );
      if (error) {
        console.error({ error });
        return;
      }
      const { data } = res;
      if (!data) {
        this.setDefaultVideoMetaData();
        return;
      }

      this.setVideoMetaData(data);
      this.$emit('stopLoading');
    },

    setVideoMetaData(data) {
      let { duration, fps, total_frames } = data;

      this.totalFrames = total_frames;
      this.videoMetaData = {
        fps,
        total_frames,
        duration,
      };
    },

    setDefaultVideoMetaData() {
      if (!this.selectedVideo) return;
      const { fps, duration } = this.selectedVideo;
      this.totalFrames = getNumberOfFrames(duration, fps);
    },

    selectFrame(item = null) {
      var _item = item ? item : this.saveFrames[this.hoveredFrameIndex];
      if (this.selectedFrame === _item) {
        this.selectedFrame = null;
        this.handleFrameChange(this.prevCurrectFrame);
        const frameObject = {
          style: {
            color: '#40a9ff',
          },
          label: `${_item}`,
        };
        this.markFrames[_item] = frameObject;
      } else {
        this.selectedFrame = _item;
        this.prevCurrectFrame = this.currentFrame;
        this.handleFrameChange(_item);
        const frameObject = {
          style: {
            color: '#4287f5',
          },
          label: `${_item}`,
        };
        this.markFrames[_item] = frameObject;
      }
    },
    navigateFrames(direction) {
      // Navigate through the frames using arrow keys
      const listLength = this.saveFrames.length;
      this.hoveredFrameIndex =
        (this.hoveredFrameIndex + direction + listLength) % listLength;
    },
    setTaskDetails(taskDetail, getData) {
      const temp = getStepsList(taskDetail);
      this.taskName = temp?.name || '';
    },
    async selectVideo(video, index) {
      this.selectedVideo = video;
      this.currentFrame = 0;
      this.currentTime = 0;
      this.currentVideoIndex = index;
      this.currentSliderPercent = 0;
      this.isMarking = false;
      this.videoLoaded = false;
      this.videoError = false;
      await this.getVideoMetaData();
      if (this.isPlaying) this.toggleVideo();
    },

    addPoint(pointType) {
      const videoName = this.selectedVideo.fileName;
      const frame = this.currentFrame;
      if (!this.mergeVideoPoints[videoName]) {
        this.mergeVideoPoints[videoName] = {};
      }

      if (
        !this.validDuration(pointType, frame, this.mergeVideoPoints[videoName])
      )
        return;
      this.mergeVideoPoints[videoName][pointType] = frame;
      if (
        pointType === frameType.startFrame &&
        !this.mergeVideoPoints[videoName][frameType.endFrame]
      )
        this.isMarking = true;
      else this.isMarking = false;
    },

    validDuration(pointType, frame, videoPoints) {
      const videoFPS = this.videoMetaData?.frameRate || this.selectedVideo?.fps;

      const showToast = () => {
        this.toast.info('Video interval should not be less than 1 second!');
      };

      if (pointType === frameType.endFrame) {
        const start = videoPoints[frameType.startFrame];
        const diff = frame - start;
        if (diff <= 0 || (diff > 0 && diff < videoFPS)) {
          showToast();
          return false;
        }

        return true;
      } else {
        if (
          !Object.prototype.hasOwnProperty.call(videoPoints, frameType.endFrame)
        ) {
          return true;
        }

        const end = videoPoints[frameType.endFrame];
        const diff = end - frame;

        if (diff <= 0 || (diff > 0 && diff < videoFPS)) {
          showToast();
          return false;
        }

        return true;
      }
    },

    allVideosHaveTwoPoints() {
      return this.videos.every((video) => {
        const points = this.mergeVideoPoints[video.fileName];
        return (
          points &&
          frameType.startFrame in points &&
          frameType.endFrame in points
        );
      });
    },

    isSequenceVideoMergeValid() {
      const firstVideo = this.videos[0];
      const secondVideo = this.videos[1];
      if (
        !Object.prototype.hasOwnProperty.call(
          this.mergeVideoPoints,
          firstVideo.fileName
        )
      )
        return false;
      if (
        !Object.prototype.hasOwnProperty.call(
          this.mergeVideoPoints,
          secondVideo.fileName
        )
      )
        return false;
      if (
        !Object.prototype.hasOwnProperty.call(
          this.mergeVideoPoints[firstVideo.fileName],
          frameType.startFrame
        )
      )
        return false;
      if (
        !Object.prototype.hasOwnProperty.call(
          this.mergeVideoPoints[secondVideo.fileName],
          frameType.endFrame
        )
      )
        return false;
      return true;
    },

    async processMerge(reverseOrder = false) {
      this.sequenceMergeConfirmModal = false;
      const folderPath = `${this.taskName}/Videos/`;
      const payload = {
        organization: this.organization,
        task: this.taskId,
        sequence_merge: this.videoSequenceMerge,
        videos: this.videos.map((video) => {
          const frames = this.mergeVideoPoints[video.fileName] || {};
          return {
            id: video.id,
            path: `${folderPath}${video.fileName}`,
            start_frame: reverseOrder ? null : frames?.start_frame,
            end_frame: reverseOrder ? null : frames?.end_frame,
          };
        }),
      };
      // api call
      const [error] = await VideoService.mergeVideoSequences(payload);
      if (error) {
        return this.toast.error('Failed to merge videos.');
      }
      this.toast.info('Videos are being merged. Please check in a while');
      this.$emit('closeModal');
    },

    async handleMergeVideos() {
      if (!this.videoSequenceMerge && !this.allVideosHaveTwoPoints()) {
        this.toast.error('Please mark start and end frames of all videos');
        return;
      }
      if (this.videoSequenceMerge) {
        if (!this.isSequenceVideoMergeValid()) {
          this.toast.error('Please mark frames of both videos');
          return;
        }
        const firstVideoId = this.videos[0].id;
        const secondVideoId = this.videos[1].id;
        if (firstVideoId > secondVideoId) {
          this.sequenceMergeConfirmModal = true;
          return;
        }
      }
      await this.processMerge();
    },
  },
};
</script>
<style scoped>
video {
  object-fit: cover;
}

@media only screen and (max-width: 768px) {
  .annotate-top-col {
    height: 470px;
  }
  .annotate-video-col {
    height: 350px;
  }
}
@media only screen and (max-width: 400px) {
  .annotate-top-col {
    height: 520px;
  }
}
.selected-frame {
  background-color: #e6f7ff;
  border-left: 4px solid #1890ff;
  cursor: pointer;
}

.hovered-frame {
  background-color: #fafafa;
  border-left: 4px solid #ff9900;
  cursor: pointer;
}

.save-frames-list {
  height: 100%; /* Set a fixed height for the list */
  overflow-y: auto; /* Enable vertical scrolling */
}

.flex-grow-1 {
  flex-grow: 1; /* Allow this div to take up remaining space */
}

.selected-video {
  background-color: #e6f7ff;
  border-left: 4px solid #1890ff;
  cursor: pointer;
}

.hovered-video {
  background-color: #fafafa;
  border-left: 4px solid #ff9900;
  cursor: pointer;
}

.videos-list {
  height: 100%;
  overflow-y: auto;
}
.video-sidebar {
  display: flex;
  flex-direction: column;
  height: calc(96vh - 90px);
  padding: 8px;
  gap: 16px;
}

.video-list-container {
  flex-grow: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.video-list {
  flex-grow: 1;
  overflow-y: auto;
  border-radius: 8px;
}

.video-list-header {
  padding: 8px 8px;
  background: #fafafa;
  display: flex;
  justify-content: space-between;
}

.video-item {
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.video-item:hover {
  background-color: #f5f5f5;
}

.video-item-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selected-video {
  background-color: #e6f7ff;
  border-right: 3px solid #1890ff;
}

.hovered-video {
  background-color: #f5f5f5;
}

.video-controls {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0;
}

.control-buttons {
  display: flex;
  gap: 12px;
}

.control-button {
  flex: 1;
  border-radius: 6px;
}

.merge-button {
  width: 100%;
  border-radius: 6px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .video-sidebar {
    height: auto;
    max-height: 40vh;
  }

  .control-buttons {
    flex-direction: column;
    gap: 8px;
  }
}
</style>
